import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { searchEntities } from '../../lib/selectors';
import { getFramework, ROLE_VAR } from '../../data/frameworks.registry';

// ⌘K / Ctrl+K command palette (build-brief §7.3): search all entities by id, name
// and tag, keyboard-navigable. Restricted to navigable kinds (controls + ATT&CK
// techniques) so every result has a detail destination.
const NAVIGABLE_KINDS = ['control', 'offensive-technique'];

const CommandPalette = ({ open, onClose, onSelect }) => {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const results = useMemo(
    () => searchEntities(query, { kinds: NAVIGABLE_KINDS, limit: 25 }),
    [query],
  );

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [open]);
  useEffect(() => setActive(0), [query]);

  if (!open) return null;

  const choose = (entity) => entity && onSelect(entity);
  const onKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(results[active]); }
  };

  return (
    <div className="tv-palette-overlay" onClick={onClose}>
      <div className="tv-palette" onClick={(e) => e.stopPropagation()} onKeyDown={onKeyDown} role="dialog" aria-label="Command palette">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '0.5px solid var(--tv-border)' }}>
          <Search style={{ width: 16, height: 16, color: 'var(--tv-muted)' }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search controls and techniques by id, name or tag…"
            style={{ flex: 1, background: 'transparent', border: 0, outline: 'none', color: 'var(--tv-text)', fontFamily: 'var(--font-body)', fontSize: 14 }}
          />
          <span className="tv-conf" style={{ color: 'var(--tv-muted)' }}>Esc</span>
        </div>
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', maxHeight: 320, overflowY: 'auto' }}>
          {results.length === 0 ? (
            <li style={{ padding: '24px', textAlign: 'center', color: 'var(--tv-muted)', fontSize: 13 }}>
              {query ? 'No matching entities.' : 'Type to search.'}
            </li>
          ) : (
            results.map((e, i) => {
              const fw = getFramework(e.frameworkId);
              return (
                <li key={`${e.frameworkId}:${e.id}`}>
                  <button
                    onMouseEnter={() => setActive(i)}
                    onClick={() => choose(e)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                      padding: '9px 16px', textAlign: 'left', cursor: 'pointer', border: 0,
                      background: i === active ? 'var(--tv-surface-2)' : 'transparent', color: 'var(--tv-text)',
                    }}
                  >
                    <span className="tv-rid" style={{ width: 96, color: ROLE_VAR[fw?.semanticRole] || 'var(--tv-text)' }}>{e.id}</span>
                    <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</span>
                    <span className="tv-conf" style={{ color: 'var(--tv-muted)' }}>{fw?.shortLabel || e.frameworkId}</span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
};

export default CommandPalette;
